import Button from "@/components/Button";
import { DateInput, SelectInput } from "@/components/InputFields";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/chadcn/Dialog";
import { Label } from "@/components/chadcn/Label";
import { Textarea } from "@/components/chadcn/TextArea";
import { useOffboardPatientMutation } from "@/data/queries";
import { Patient, PatientOffboardingReason } from "@/model/Patient";
import { LocalDate, ZoneId } from "@js-joda/core";
import { useEffect } from "react";
import { useController, useForm } from "react-hook-form";

type OffboardPatientDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: Patient;
  prescriptionId: string;
  deliveryDate?: LocalDate;
  onConfirm?: () => void;
};

type OffboardReasonOption = {
  value: PatientOffboardingReason | "";
  title: string;
  disabled?: boolean;
};

type OffboardFormData = {
  reason: PatientOffboardingReason | "";
  deliveryDate: string;
  note: string;
};

const OffboardPatientDialog = ({
  open,
  onOpenChange,
  patient,
  prescriptionId,
  deliveryDate,
  onConfirm,
}: OffboardPatientDialogProps) => {
  const { mutate: offboardPatient, isPending: isLoading } = useOffboardPatientMutation({
    patientId: patient.id,
  });

  const { control, reset, handleSubmit, watch, register, formState } = useForm<OffboardFormData>({
    defaultValues: {
      reason: "",
      deliveryDate: deliveryDate?.toString() || "",
      note: "",
    },
  });

  const {
    field: reasonField,
    fieldState: { error: reasonError },
  } = useController({
    name: "reason",
    control: control,
    rules: { required: "Please select a reason for offboarding" },
  });

  const { field: deliveryDateField } = useController({
    name: "deliveryDate",
    control: control,
  });

  const handleSubmitForm = handleSubmit((data) => {
    offboardPatient(
      {
        prescriptionId,
        reason: data.reason as PatientOffboardingReason,
        deliveryDate: data.deliveryDate ? LocalDate.parse(data.deliveryDate) : undefined,
        note: data.note || undefined,
      },
      {
        onSuccess: () => {
          onConfirm?.();
          handleOpenChange(false);
        },
      }
    );
  });

  const handleOpenChange = (newOpen: boolean) => {
    if (!isLoading) {
      onOpenChange(newOpen);
      if (!newOpen) {
        reset();
      }
    }
  };

  const handleCancel = () => {
    handleOpenChange(false);
  };

  useEffect(() => {
    if (open) {
      reset({
        reason: "",
        deliveryDate: deliveryDate?.toString() || "",
        note: "",
      });
    }
  }, [open, deliveryDate, reset]);

  const reasonOptions: OffboardReasonOption[] = [
    { value: "", title: "Select an option", disabled: true },
    { value: "patient-completed-protocol", title: "Delivered, postpartum" },
    { value: "patient-non-compliant", title: "Patient noncompliant" },
    { value: "patient-declined", title: "Patient declined" },
    { value: "patient-transferred", title: "Patient transferred" },
    {
      value: "patient-end-program-early",
      title: "Patient end program early",
    },
    {
      value: "device-connectivity-issue",
      title: "Device connectivity issue",
    },
    { value: "other", title: "Other" },
  ];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex min-w-[720px] flex-col gap-y-4 bg-white">
        <DialogHeader>
          <DialogTitle className="mb-2">Offboard Patient</DialogTitle>
          <DialogDescription>
            {patient.firstName} {patient.lastName}
          </DialogDescription>
        </DialogHeader>
        <form id="offboard-form" onSubmit={handleSubmitForm} className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="reason-select" className="text-sm font-medium">
              Reason for offboarding <span className="text-red-500">*</span>
            </label>
            <SelectInput
              id="reason-select"
              value={reasonField.value}
              options={reasonOptions}
              onChange={reasonField.onChange}
              disabled={isLoading}
            />
            {reasonError && <span className="text-sm text-red-500">{reasonError.message}</span>}
          </div>
          <div className="grid gap-2">
            <label htmlFor="delivery-date-input" className="text-sm font-medium">
              Delivery date
            </label>
            <DateInput
              id="delivery-date-input"
              value={deliveryDateField.value}
              onChange={deliveryDateField.onChange}
              disabled={isLoading}
              className="w-full"
              max={LocalDate.now(ZoneId.SYSTEM).toString()}
            />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="note-input" className="text-sm font-medium">
                Note
              </Label>
              <span className="text-xs text-gray-500">{watch("note").length}/2000</span>
            </div>
            <Textarea
              id="note-input"
              {...register("note", { maxLength: 2000 })}
              disabled={isLoading}
              className="h-32 border-gray-300"
              placeholder="Enter any additional notes about the offboarding..."
              maxLength={2000}
            />
          </div>
        </form>
        <DialogFooter>
          <div className="flex gap-2">
            <Button
              variant="neutral"
              onClick={handleCancel}
              disabled={isLoading}
              className="w-fit border-none bg-primary/10"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              type="submit"
              form="offboard-form"
              disabled={isLoading || !formState.isValid}
              className="w-fit"
            >
              {isLoading ? "Offboarding..." : "Offboard Patient"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OffboardPatientDialog;
